import { readonly, ref } from 'vue';
import type { AxiosError } from 'axios';
import i18n from '@/plugins/vue-i18n';
import { useToastStore } from '@/stores/utils/toast.store';
import { useVersionConflictStore } from '@/stores/utils/version-conflict.store';
import type { RequestConfig } from '@/types/api/response.types';

/**
 * The `meta.kind` of the API's refusal to cancel or decline an order while a payment awaiting its
 * proof holds a request on it (D-30). Every other 409 on those writes is a conflict.
 */
export const AWAITING_PROOF_HOLD_KIND = 'AwaitingProofHold';

/**
 * Context: an order cancel and a payment request decline answer 409 for two reasons. A stale
 * version or a write conflict is the shared reload dialog's, but the API also refuses either while
 * a payment awaiting its proof holds a request on the order, naming that payment (D-30). The dialog
 * would swallow the refusal, so these writes keep their 409s from it and `sayHandledConflict`
 * tells them apart.
 */
export const orderEndingWriteConfig: Partial<RequestConfig> = { handlesConflict: true };

const awaitingProofRefusalCount = ref(0);

/**
 * Counts the D-30 refusals this session has met, so a hold read on screen asks again and an open
 * confirm closes its action with the payment named.
 */
export const awaitingProofRefusals = readonly(awaitingProofRefusalCount);

type ConflictBody = { meta?: { kind?: string; message?: string } };

/**
 * The server's words when a 409 is the D-30 refusal, or `undefined` for any other answer, which is
 * read as the conflict it has always been.
 */
export const awaitingProofRefusalMessage = (error: unknown): string | undefined => {
  const response = (error as AxiosError<ConflictBody> | undefined)?.response;
  const meta = response?.data?.meta;
  return response?.status === 409 && meta?.kind === AWAITING_PROOF_HOLD_KIND
    ? meta.message?.trim() || undefined
    : undefined;
};

/**
 * Lets every hold read on screen ask again when an answer is the D-30 refusal, for a caller that
 * reads its own 409s (the bulk order cancel and the bulk request decline say it in their confirms).
 */
export const noteAwaitingProofRefusal = (error: unknown): void => {
  if (awaitingProofRefusalMessage(error)) awaitingProofRefusalCount.value += 1;
};

/**
 * Says a 409 that its request kept from the shared reload dialog (`handlesConflict`) and its caller
 * does not read itself: the D-30 refusal toasts the server's own words, and every other 409 raises
 * the dialog, as the interceptor would have.
 */
export const sayHandledConflict = (error: unknown): void => {
  const { response, config } = (error ?? {}) as AxiosError<ConflictBody>;
  if (response?.status !== 409 || !(config as RequestConfig | undefined)?.handlesConflict) return;

  const refusal = awaitingProofRefusalMessage(error);

  if (refusal) {
    useToastStore().showToast(
      'error',
      i18n.global.t('error.toast.awaitingProofHold.title'),
      refusal,
    );
    noteAwaitingProofRefusal(error);
    return;
  }

  useVersionConflictStore().show();
};
